import React, { useEffect, useState } from 'react';
import { useSocket } from '../../contexts/SocketContext';
import './AlertNotifications.css';

const AlertNotifications = () => {
  const { alerts, dismissAlert, markAlertAsRead, isConnected } = useSocket();
  const [visibleAlerts, setVisibleAlerts] = useState([]);
  const [soundEnabled, setSoundEnabled] = useState(true);

  // Show only the most recent unread alerts (max 3 at once)
  useEffect(() => {
    const unreadAlerts = alerts.filter(alert => !alert.read).slice(0, 3);
    setVisibleAlerts(unreadAlerts);

    // Play sound for high priority alerts
    if (soundEnabled && unreadAlerts.some(a => a.priority === 'critical' || a.priority === 'high')) {
      playAlertSound(unreadAlerts[0]?.priority);
    }
  }, [alerts, soundEnabled]);

  const playAlertSound = (priority) => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      // Different frequencies for different priorities
      oscillator.frequency.value = priority === 'critical' ? 800 : 600;
      gainNode.gain.value = 0.3;

      oscillator.start();
      setTimeout(() => oscillator.stop(), 200);
    } catch (error) {
      console.error('Error playing alert sound:', error);
    }
  };

  const handleDismiss = (alertId) => {
    markAlertAsRead(alertId);
    dismissAlert(alertId);
  };

  const getAlertIcon = (type, severity) => {
    if (type === 'emergency-alert' || severity === 'critical') return '🚨';
    if (type === 'harassment-alert') return '⚠️';
    if (type === 'incident-alert') {
      if (severity === 'high') return '🔴';
      if (severity === 'medium') return '🟡';
      return '🔵';
    }
    if (type === 'zone-update') return '🗺️';
    return '📢';
  };

  const getAlertTitle = (alert) => {
    switch (alert.type) {
      case 'incident-alert':
        return `Safety Alert: ${alert.incidentType || 'Incident'} Nearby`;
      case 'harassment-alert':
        return 'Harassment Alert Nearby';
      case 'emergency-alert':
        return '🚨 EMERGENCY SOS';
      case 'zone-update':
        return `Safety Zone Update: ${alert.zoneName}`;
      case 'verification-update':
        return 'Report Verified';
      case 'incident-status-update':
        return 'Incident Status Updated';
      default:
        return 'Safety Alert';
    }
  };

  const getAlertMessage = (alert) => {
    switch (alert.type) {
      case 'incident-alert':
        return `${alert.summary || 'An incident has been reported'} - ${alert.location?.address || 'Location nearby'}`;
      case 'harassment-alert':
        return `${alert.harassmentTypes?.join(', ') || 'Harassment'} reported at ${alert.locationName || 'nearby location'}. ${alert.safetyTip || ''}`;
      case 'emergency-alert':
        return `Emergency SOS from nearby user. ${alert.message || ''} Please be aware and stay safe.`;
      case 'zone-update':
        return `Safety level: ${alert.safetyLevel || 'Unknown'}. ${alert.alerts?.length || 0} active alerts.`;
      default:
        return alert.summary || 'A safety alert has been issued for your area.';
    }
  };

  const getPriorityClass = (alert) => {
    if (alert.type === 'emergency-alert' || alert.priority === 'critical' || alert.severity === 'critical') {
      return 'alert-critical';
    }
    if (alert.priority === 'high' || alert.severity === 'high') {
      return 'alert-high';
    }
    if (alert.priority === 'medium' || alert.severity === 'medium') {
      return 'alert-medium';
    }
    return 'alert-low';
  };

  if (!isConnected) {
    return (
      <div className="connection-status offline">
        <span className="status-dot"></span>
        <span className="status-text">Reconnecting to real-time alerts...</span>
      </div>
    );
  }

  return (
    <div className="alert-notifications-container">
      {visibleAlerts.map((alert, index) => (
        <div
          key={alert.id}
          className={`alert-notification ${getPriorityClass(alert)} slide-in`}
          style={{ top: `${80 + index * 110}px` }}
        >
          <div className="alert-header">
            <span className="alert-icon">{getAlertIcon(alert.type, alert.severity)}</span>
            <span className="alert-title">{getAlertTitle(alert)}</span>
            <button
              className="alert-close"
              onClick={() => handleDismiss(alert.id)}
              aria-label="Dismiss alert"
            >
              ×
            </button>
          </div>
          <div className="alert-body">
            <p className="alert-message">{getAlertMessage(alert)}</p>
            {alert.distance && (
              <p className="alert-distance">
                📍 {Math.round(alert.distance)}m away
              </p>
            )}
            <p className="alert-time">
              {formatTimeAgo(alert.timestamp)}
            </p>
          </div>
        </div>
      ))}

      {/* Connection Status Indicator (subtle) */}
      {isConnected && visibleAlerts.length === 0 && (
        <div className="connection-status online">
          <span className="status-dot"></span>
          <span className="status-text">Live alerts active</span>
        </div>
      )}
    </div>
  );
};

const formatTimeAgo = (timestamp) => {
  const seconds = Math.floor((new Date() - new Date(timestamp)) / 1000);

  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
};

export default AlertNotifications;
